/**
 * @fileoverview Branding Settings
 *
 * Purpose: Upload a branded PDF, preview extracted letterhead header/footer
 * with adjustable crop sensitivity, then save the final images.
 * URL: /dashboard/settings/branding
 */

import { createFileRoute } from "@tanstack/react-router";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "~/convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useActiveOrg } from "@/utils/org-context";
import { Spinner } from "@/ui/primitives";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RotateCcw,
  Palette,
  Type,
  Plus,
  X,
  Lock,
  WrapText,
  ALargeSmall,
  Rows3,
  LayoutGrid,
  FileImage,
  SlidersHorizontal,
  Ruler,
} from "lucide-react";
import { cn } from "@/utils/misc";
import type { Id } from "~/convex/_generated/dataModel";
import { toast } from "sonner";
import {
  resolveDocumentPadding,
  DEFAULT_PADDING,
  type DocumentPadding,
  type PaddingSource,
  type PageRule,
} from "@/utils/branding/resolve-letterhead";
import { LetterheadVariantsManager } from "@/components/branding/LetterheadVariantsManager";
import { LetterheadAssetsManager } from "@/components/branding/LetterheadAssetsManager";
import { PageNumberingCard } from "@/components/branding/PageNumberingCard";
import { MarginsRulerDiagram } from "@/components/branding/MarginsRulerDiagram";
import { LetterheadSamplePreview } from "@/components/branding/LetterheadSamplePreview";
import { diagramValuesToPadding } from "@/utils/branding/samplePreview";
import {
  buildBundleFragments,
  buildVariantFragments,
  type LayeredVariantLike,
  type LetterheadAssetLike,
} from "@/utils/branding/letterheadFragments";
import {
  formatForUnit,
  parseInForUnit,
  useMarginUnit,
  type MarginUnit,
} from "@/utils/branding/marginUnits";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ColorPickerPopover } from "@/ui/composed/inputs/color-picker-popover";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  DOCUMENT_FONT_OPTIONS,
  FONT_SIZES,
} from "@/ui/composed/editor/font-options";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

const DEFAULT_PRESET_COLORS = [
  "#000000",
  "#333333",
  "#666666",
  "#1B3A5C",
  "#1a56db",
  "#047857",
  "#8B1A1A",
  "#b91c1c",
  "#d97706",
  "#7c3aed",
];

/**
 * Default body text colour applied when an org has not customised it.
 * Pure black matches Word's default and historically PDF form fills, so
 * the editor preview, browser PDF, and exported .docx all agree.
 */
const DEFAULT_BODY_TEXT_COLOR = "#000000";

/**
 * Line-spacing options surfaced in the branding card. Must stay in sync
 * with `LINE_SPACINGS` in `template-editor.tsx` so the toolbar's per-paragraph
 * picker mirrors the org defaults. Empty `value` means "no override" (the
 * pipeline falls back to 1.6).
 */
const EDITOR_LINE_HEIGHT_OPTIONS = [
  { label: "Default (1.6)", value: "" },
  { label: "1.0", value: "1" },
  { label: "1.15", value: "1.15" },
  { label: "1.5", value: "1.5" },
  { label: "1.75", value: "1.75" },
  { label: "2.0", value: "2" },
  { label: "2.5", value: "2.5" },
  { label: "3.0", value: "3" },
] as const;

/**
 * Body font-size options for the branding default. Reuses the toolbar's
 * per-paragraph FONT_SIZES list so the org default and the per-paragraph
 * picker stay aligned. The "Default" row maps to an empty value so the
 * pipeline falls back to 11pt.
 */
const EDITOR_FONT_SIZE_OPTIONS = [
  { label: "Default (11pt)", value: "" },
  ...FONT_SIZES.filter((s) => s.value !== ""),
] as const;

/**
 * Default paragraph space-before / space-after options. Empty `value`
 * means "no default" — paragraphs render with the editor's intrinsic
 * margins. Per-paragraph overrides from the ParagraphSpacing extension
 * still win.
 */
const EDITOR_PARAGRAPH_SPACING_OPTIONS = [
  { label: "Default (none)", value: "" },
  { label: "0pt", value: "0pt" },
  { label: "4pt", value: "4pt" },
  { label: "6pt", value: "6pt" },
  { label: "8pt", value: "8pt" },
  { label: "12pt", value: "12pt" },
  { label: "16pt", value: "16pt" },
  { label: "24pt", value: "24pt" },
] as const;

interface BrandingProfilePreview {
  sourceDocumentName?: string | null;
  extractionStatus?: "pending" | "extracting" | "completed" | "failed" | null;
  headerImageUrl?: string | null;
  footerImageUrl?: string | null;
  leftSideImageUrl?: string | null;
  rightSideImageUrl?: string | null;
  headerImageScale?: number | null;
  footerImageScale?: number | null;
  leftSideImageScale?: number | null;
  rightSideImageScale?: number | null;
  headerHtml?: string | null;
  footerHtml?: string | null;
  headerPageRule?: PageRule | null;
  footerPageRule?: PageRule | null;
  leftSidePageRule?: PageRule | null;
  rightSidePageRule?: PageRule | null;
  headerTopPadding?: number | null;
  headerSidePadding?: number | null;
  bodyTopPadding?: number | null;
  bodySidePadding?: number | null;
  footerBottomPadding?: number | null;
  footerSidePadding?: number | null;
  sidePadding?: number | null;
  editorPresetColors?: string[] | null;
  editorFontFamily?: string | null;
  editorLineHeight?: string | null;
  editorFontSize?: string | null;
  editorParagraphSpacingBefore?: string | null;
  editorParagraphSpacingAfter?: string | null;
  bodyTextColor?: string | null;
}

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/branding",
)({
  component: BrandingSettings,
  beforeLoad: () => ({
    title: "Branding",
    headerTitle: "Settings",
  }),
});

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * Branding settings is organised into focused sub-tabs so practitioners
 * can jump straight to the area they want to edit (saves scrolling past
 * unrelated cards on every visit). Mirrors the precedent library's
 * category tab bar (see _layout.settings.docs.tsx) for visual consistency.
 *
 * - "All": full original layout (letterheads + variants → editor
 *   preferences → margins). Default landing tab so first-time users still
 *   see everything.
 * - "Letterheads": upload card + saved letterhead previews + variants.
 * - "Editor preferences": fonts, colours, line height defaults.
 * - "Document margins": padding sliders per zone.
 */
type BrandingTabId = "all" | "letterhead" | "editor" | "margins";

const BRANDING_TABS: Array<{
  id: BrandingTabId;
  label: string;
  icon: typeof LayoutGrid;
}> = [
  { id: "all", label: "All", icon: LayoutGrid },
  { id: "letterhead", label: "Letterheads", icon: FileImage },
  { id: "editor", label: "Editor preferences", icon: SlidersHorizontal },
  { id: "margins", label: "Document margins", icon: Ruler },
];

export default function BrandingSettings() {
  const { orgId, activeOrg } = useActiveOrg();
  const canEdit = activeOrg?.role === "owner" || activeOrg?.role === "admin";
  const [activeTab, setActiveTab] = useState<BrandingTabId>("all");

  const { data: profile, isLoading } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  if (!orgId) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <p className="text-sm text-primary/60">
          Select an organisation to configure branding.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Spinner className="size-6" />
      </div>
    );
  }

  const typedOrgId = orgId as Id<"organizations">;

  const showLetterheads = activeTab === "all" || activeTab === "letterhead";
  const showEditor = activeTab === "all" || activeTab === "editor";
  const showMargins = activeTab === "all" || activeTab === "margins";

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      {!canEdit && <ReadOnlyBanner area="branding" />}

      <BrandingTabBar activeTab={activeTab} onChange={setActiveTab} />

      {showLetterheads && (
        <div className={GROUP_CARD_CLASS}>
          <LetterheadAssetsManager orgId={typedOrgId} canEdit={canEdit} />
          <LetterheadVariantsManager
            orgId={typedOrgId}
            canEdit={canEdit}
            showDivider
          />
          <PageNumberingCard
            orgId={typedOrgId}
            profile={profile}
            canEdit={canEdit}
          />
        </div>
      )}

      {showEditor && (
        <EditorPreferencesSection
          orgId={typedOrgId}
          profile={profile}
          canEdit={canEdit}
        />
      )}

      {showMargins && (
        <DocumentMarginsSection
          orgId={typedOrgId}
          profile={profile}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

/**
 * Pill-style tab bar used at the top of the branding settings page. Lifted
 * directly from the precedent library's category tab pattern (see
 * `_layout.settings.docs.tsx`) so the two settings pages feel like
 * siblings.
 */
function BrandingTabBar({
  activeTab,
  onChange,
}: {
  activeTab: BrandingTabId;
  onChange: (tab: BrandingTabId) => void;
}) {
  return (
    <div className="flex gap-1 overflow-x-auto rounded-lg bg-secondary/40 p-1">
      {BRANDING_TABS.map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
              activeTab === tab.id
                ? "bg-background text-primary shadow-sm"
                : "text-primary/50 hover:text-primary/70",
            )}
          >
            <Icon className="size-3.5" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Inline banner shown when an org `member` visits a settings page that requires
 * `owner` or `admin` access to edit. Communicates the read-only state without
 * removing read access (preview, current values, etc. all stay visible).
 */
function ReadOnlyBanner({ area }: { area: "branding" | "precedent library" }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
      <Lock className="mt-0.5 size-4 shrink-0" />
      <div className="text-sm">
        <p className="font-medium">Read-only view</p>
        <p className="mt-0.5 text-xs opacity-80">
          You need owner or admin access to edit {area} settings. Ask an admin
          in your organisation to make changes.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document Margins Section
// ---------------------------------------------------------------------------

type MarginKey =
  | "headerTopPadding"
  | "headerLeftPadding"
  | "headerRightPadding"
  | "bodyTopPadding"
  | "bodyLeftPadding"
  | "bodyRightPadding"
  | "bodyBottomPadding"
  | "footerBottomPadding"
  | "footerLeftPadding"
  | "footerRightPadding";

const MARGIN_ZONES: Array<{
  zone: string;
  fields: Array<{
    key: MarginKey;
    label: string;
    defaultKey: keyof typeof DEFAULT_PADDING;
  }>;
}> = [
  {
    zone: "Header",
    fields: [
      { key: "headerTopPadding", label: "Top", defaultKey: "headerTop" },
      { key: "headerLeftPadding", label: "Left", defaultKey: "headerLeft" },
      { key: "headerRightPadding", label: "Right", defaultKey: "headerRight" },
    ],
  },
  {
    zone: "Body",
    fields: [
      { key: "bodyTopPadding", label: "Top", defaultKey: "bodyTop" },
      { key: "bodyLeftPadding", label: "Left", defaultKey: "bodyLeft" },
      { key: "bodyRightPadding", label: "Right", defaultKey: "bodyRight" },
      { key: "bodyBottomPadding", label: "Bottom", defaultKey: "bodyBottom" },
    ],
  },
  {
    zone: "Footer",
    fields: [
      {
        key: "footerBottomPadding",
        label: "Bottom",
        defaultKey: "footerBottom",
      },
      { key: "footerLeftPadding", label: "Left", defaultKey: "footerLeft" },
      { key: "footerRightPadding", label: "Right", defaultKey: "footerRight" },
    ],
  },
];

const ALL_MARGIN_KEYS = MARGIN_ZONES.flatMap((z) => z.fields.map((f) => f.key));

/** Resolved padding → the nine editable px values. */
function marginValuesFromPadding(
  p: DocumentPadding,
): Record<MarginKey, number> {
  return {
    headerTopPadding: p.headerTop,
    headerLeftPadding: p.headerLeft,
    headerRightPadding: p.headerRight,
    bodyTopPadding: p.bodyTop,
    bodyLeftPadding: p.bodyLeft,
    bodyRightPadding: p.bodyRight,
    bodyBottomPadding: p.bodyBottom,
    footerBottomPadding: p.footerBottom,
    footerLeftPadding: p.footerLeft,
    footerRightPadding: p.footerRight,
  };
}

/**
 * The legacy symmetric `*SidePadding` field that mirrors a Left/Right key,
 * so readers that predate per-side margins still see a sensible value.
 */
function legacySideKeyFor(
  key: MarginKey,
): "headerSidePadding" | "bodySidePadding" | "footerSidePadding" | null {
  if (key.startsWith("header") && key !== "headerTopPadding")
    return "headerSidePadding";
  if (
    key.startsWith("body") &&
    key !== "bodyTopPadding" &&
    key !== "bodyBottomPadding"
  )
    return "bodySidePadding";
  if (key.startsWith("footer") && key !== "footerBottomPadding")
    return "footerSidePadding";
  return null;
}

function DocumentMarginsSection({
  orgId,
  profile,
  canEdit,
}: {
  orgId: Id<"organizations">;
  profile: BrandingProfilePreview | null | undefined;
  canEdit: boolean;
}) {
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.mutations.updateBrandingProfile,
    ),
  });

  const [unit, setUnit] = useMarginUnit();

  const padding = resolveDocumentPadding(profile);

  // Firm default letterhead (variant wins over bundle) — when set, margins
  // are edited by dragging edges over a live sample page instead of the
  // abstract ruler diagram.
  const { data: marginAssets } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const { data: marginVariants } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const firmFragments = useMemo(() => {
    const p = profile as
      | (BrandingProfilePreview & {
          firmDefaultLetterheadVariantId?: string | null;
          firmDefaultLetterheadBundleName?: string | null;
        })
      | null
      | undefined;
    const variantId = p?.firmDefaultLetterheadVariantId ?? null;
    if (variantId) {
      const variants = (marginVariants ?? []) as Array<
        LayeredVariantLike & { _id: string }
      >;
      const variant = variants.find((v) => v._id === variantId);
      if (variant) return buildVariantFragments(variant);
    }
    const bundleName = p?.firmDefaultLetterheadBundleName ?? null;
    if (bundleName) {
      return buildBundleFragments(
        bundleName,
        (marginAssets ?? []) as LetterheadAssetLike[],
      );
    }
    return [];
  }, [profile, marginAssets, marginVariants]);

  const formatAll = useCallback(
    (px: Record<MarginKey, number>): Record<MarginKey, string> =>
      Object.fromEntries(
        ALL_MARGIN_KEYS.map((k) => [k, formatForUnit(px[k], unit)]),
      ) as Record<MarginKey, string>,
    [unit],
  );

  // Source of truth for "what's saved" — kept in px to match storage.
  const [committedPx, setCommittedPx] = useState<Record<MarginKey, number>>(
    () => marginValuesFromPadding(padding),
  );

  // Per-field string drafts so users can clear the input mid-edit (MOR-211).
  const [drafts, setDrafts] = useState<Record<MarginKey, string>>(() =>
    formatAll(marginValuesFromPadding(padding)),
  );

  // Re-seed both committed values and drafts when the saved padding or the
  // unit changes. The stored padding fields are hoisted to scalars so the
  // effect re-runs only when a saved margin actually changes — depending on
  // the whole profile object would clobber in-flight edits whenever any
  // unrelated branding field updates.
  const profilePadding: PaddingSource | null | undefined = profile;
  const {
    headerTopPadding,
    headerSidePadding,
    headerLeftPadding,
    headerRightPadding,
    bodyTopPadding,
    bodySidePadding,
    bodyLeftPadding,
    bodyRightPadding,
    bodyBottomPadding,
    footerBottomPadding,
    footerSidePadding,
    footerLeftPadding,
    footerRightPadding,
    sidePadding,
  } = profilePadding ?? {};
  useEffect(() => {
    const next = marginValuesFromPadding(
      resolveDocumentPadding({
        headerTopPadding,
        headerSidePadding,
        headerLeftPadding,
        headerRightPadding,
        bodyTopPadding,
        bodySidePadding,
        bodyLeftPadding,
        bodyRightPadding,
        bodyBottomPadding,
        footerBottomPadding,
        footerSidePadding,
        footerLeftPadding,
        footerRightPadding,
        sidePadding,
      }),
    );
    setCommittedPx(next);
    setDrafts(formatAll(next));
  }, [
    headerTopPadding,
    headerSidePadding,
    headerLeftPadding,
    headerRightPadding,
    bodyTopPadding,
    bodySidePadding,
    bodyLeftPadding,
    bodyRightPadding,
    bodyBottomPadding,
    footerBottomPadding,
    footerSidePadding,
    footerLeftPadding,
    footerRightPadding,
    sidePadding,
    formatAll,
  ]);

  const commitField = useCallback(
    async (key: MarginKey) => {
      const raw = drafts[key];
      const result = parseInForUnit(raw, unit);

      if (result.kind === "empty") {
        // Snap back to last committed value if the user blanked the field.
        setDrafts((prev) => ({
          ...prev,
          [key]: formatForUnit(committedPx[key], unit),
        }));
        return;
      }
      if (result.kind === "invalid" || result.kind === "out_of_range") {
        toast.error(result.reason);
        setDrafts((prev) => ({
          ...prev,
          [key]: formatForUnit(committedPx[key], unit),
        }));
        return;
      }

      const nextPx = result.px;
      if (nextPx === committedPx[key]) {
        // No change, just renormalise the displayed value.
        setDrafts((prev) => ({ ...prev, [key]: formatForUnit(nextPx, unit) }));
        return;
      }

      const nextCommitted = { ...committedPx, [key]: nextPx };
      setCommittedPx(nextCommitted);
      setDrafts((prev) => ({ ...prev, [key]: formatForUnit(nextPx, unit) }));
      try {
        const legacyKey = legacySideKeyFor(key);
        const legacyPatch = legacyKey
          ? {
              [legacyKey]: Math.min(
                nextCommitted[
                  `${legacyKey.replace("Side", "Left")}` as MarginKey
                ],
                nextCommitted[
                  `${legacyKey.replace("Side", "Right")}` as MarginKey
                ],
              ),
            }
          : {};
        await updateProfile({ orgId, [key]: nextPx, ...legacyPatch });
      } catch {
        toast.error("Failed to update margin.");
      }
    },
    [drafts, unit, committedPx, updateProfile, orgId],
  );

  const handleReset = async () => {
    try {
      await updateProfile({
        orgId,
        headerTopPadding: DEFAULT_PADDING.headerTop,
        headerSidePadding: DEFAULT_PADDING.headerSide,
        headerLeftPadding: DEFAULT_PADDING.headerLeft,
        headerRightPadding: DEFAULT_PADDING.headerRight,
        bodyTopPadding: DEFAULT_PADDING.bodyTop,
        bodySidePadding: DEFAULT_PADDING.bodySide,
        bodyLeftPadding: DEFAULT_PADDING.bodyLeft,
        bodyRightPadding: DEFAULT_PADDING.bodyRight,
        bodyBottomPadding: DEFAULT_PADDING.bodyBottom,
        footerBottomPadding: DEFAULT_PADDING.footerBottom,
        footerSidePadding: DEFAULT_PADDING.footerSide,
        footerLeftPadding: DEFAULT_PADDING.footerLeft,
        footerRightPadding: DEFAULT_PADDING.footerRight,
      });
      toast.success("Margins reset to defaults");
    } catch {
      toast.error("Failed to reset margins.");
    }
  };

  // Ruler-diagram drags: live-update the committed px (so inputs + diagram
  // follow the pointer), then persist on release through the same patch the
  // text inputs use, including the legacy symmetric mirror.
  const handleDiagramChange = useCallback(
    (key: MarginKey, px: number) => {
      setCommittedPx((prev) => ({ ...prev, [key]: px }));
      setDrafts((prev) => ({ ...prev, [key]: formatForUnit(px, unit) }));
    },
    [unit],
  );
  const handleDiagramCommit = useCallback(
    async (key: MarginKey, px: number) => {
      const next = { ...committedPx, [key]: px };
      const legacyKey = legacySideKeyFor(key);
      const legacyPatch = legacyKey
        ? {
            [legacyKey]: Math.min(
              next[legacyKey.replace("Side", "Left") as MarginKey],
              next[legacyKey.replace("Side", "Right") as MarginKey],
            ),
          }
        : {};
      try {
        await updateProfile({ orgId, [key]: px, ...legacyPatch });
      } catch {
        toast.error("Failed to update margin.");
      }
    },
    [committedPx, updateProfile, orgId],
  );

  const isDefault = MARGIN_ZONES.every((zone) =>
    zone.fields.every(
      (f) => committedPx[f.key] === DEFAULT_PADDING[f.defaultKey],
    ),
  );

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="flex items-start justify-between gap-3 px-6 py-3">
        <div>
          <h2 className="text-base font-medium text-primary/70">
            Document Margins
          </h2>
          <p className="mt-0.5 text-xs text-primary/40">
            Controls spacing around header, body, and footer zones. Each zone
            has its own left and right margin so the letterhead can sit wider or
            narrower than the body text, or off-centre.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <UnitToggle unit={unit} onChange={setUnit} />
          {!isDefault && canEdit && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-primary/50 transition hover:bg-secondary"
            >
              <RotateCcw className="size-3" />
              Reset to defaults
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 border-t border-border/50 px-6 py-4">
        {MARGIN_ZONES.map((zone) => (
          <div key={zone.zone} className="flex flex-col gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wider text-primary/40">
              {zone.zone}
            </span>
            {zone.fields.map((field) => {
              const defaultPx = DEFAULT_PADDING[field.defaultKey];
              const isFieldDefault = committedPx[field.key] === defaultPx;
              return (
                <div key={field.key} className="flex items-center gap-2">
                  <label className="w-12 text-xs text-primary/50">
                    {field.label}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={drafts[field.key]}
                    readOnly={!canEdit}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    onBlur={() => {
                      if (canEdit) void commitField(field.key);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    className={cn(
                      "h-7 w-16 rounded-lg border border-border/50 bg-background px-1.5 text-center text-sm tabular-nums transition focus:border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/20",
                      !canEdit && "cursor-not-allowed opacity-60",
                    )}
                  />
                  <span className="text-[10px] text-primary/30">{unit}</span>
                  {!isFieldDefault && (
                    <span className="text-[10px] text-primary/25">
                      ({formatForUnit(defaultPx, unit)})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Interactive margin editing: when a firm default letterhead is set,
          drag the edges over a live sample page (real letterhead + sample
          text); otherwise fall back to the abstract A4 ruler diagram.
          Renders from committed px so it's stable while typing. */}
      <div className="border-t border-border/50 px-6 py-4">
        <div className="flex flex-col items-center gap-1">
          {firmFragments.length > 0 ? (
            <>
              <LetterheadSamplePreview
                fragments={firmFragments}
                padding={diagramValuesToPadding(committedPx)}
                pageIndex={1}
                width={397}
                editableMargins
                unit={unit}
                marginValues={committedPx}
                disabled={!canEdit}
                onMarginChange={handleDiagramChange}
                onMarginCommit={handleDiagramCommit}
              />
              {canEdit && (
                <p className="mt-1 max-w-md text-center text-[10px] text-primary/35">
                  Showing the firm default letterhead with sample text. Drag any
                  dashed edge to adjust that margin; values save when you let
                  go.
                </p>
              )}
            </>
          ) : (
            <MarginsRulerDiagram
              values={committedPx}
              unit={unit}
              disabled={!canEdit}
              onChange={handleDiagramChange}
              onCommit={handleDiagramCommit}
            />
          )}
        </div>
      </div>

      <PerLayoutMarginsSummary orgId={orgId} />
    </div>
  );
}

/**
 * Read-only pointer to variants that carry per-layout margins. The margins
 * themselves are edited on the variant card (Letterheads tab) so they flow
 * to every precedent using that variant.
 */
function PerLayoutMarginsSummary({ orgId }: { orgId: Id<"organizations"> }) {
  const { data: variants } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const customised = (
    (variants ?? []) as Array<{
      _id: string;
      name: string;
      layoutMargins?: unknown[];
    }>
  ).filter((v) => (v.layoutMargins?.length ?? 0) > 0);
  return (
    <div className="border-t border-border/50 px-6 py-3 text-xs text-primary/50">
      <p>
        Per-layout margins (different margins when a page shows a different
        header, footer or side) are set on each letterhead variant in the
        Letterheads tab.
      </p>
      {customised.length > 0 && (
        <ul className="mt-1 list-disc pl-4">
          {customised.map((v) => (
            <li key={v._id}>
              {v.name}: {v.layoutMargins?.length} customised layout
              {v.layoutMargins?.length === 1 ? "" : "s"}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UnitToggle({
  unit,
  onChange,
}: {
  unit: MarginUnit;
  onChange: (next: MarginUnit) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Margin unit"
      className="inline-flex overflow-hidden rounded-md border border-border/60 text-[11px]"
    >
      {(["cm", "px"] as const).map((u) => (
        <button
          key={u}
          type="button"
          role="radio"
          aria-checked={unit === u}
          onClick={() => onChange(u)}
          className={cn(
            "px-2 py-1 transition",
            unit === u
              ? "bg-primary/10 font-medium text-primary"
              : "text-primary/50 hover:bg-secondary hover:text-primary/70",
          )}
        >
          {u}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor Preferences Section (preset colors + default font)
// ---------------------------------------------------------------------------

/**
 * In-memory palette swatch. `id` is a stable client-side identifier used
 * solely to track items across drag-and-drop reorders; it is never
 * persisted. The persisted value is the ordered array of `color` strings
 * on `brandingProfiles.editorPresetColors`.
 */
type PaletteSwatch = { id: string; color: string };

/**
 * Generate a stable id for a palette swatch. Prefers
 * `crypto.randomUUID()` (modern browsers) and falls back to a random
 * string for older runtimes / non-secure contexts.
 */
function createSwatchId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `swatch_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

/**
 * Single draggable palette swatch. Wraps a colour-picker popover trigger
 * with dnd-kit `useSortable` so the user can drag to reorder. A short
 * activation distance on the underlying PointerSensor keeps single-clicks
 * (open picker / remove) from being misread as drag intents.
 */
function SortablePaletteSwatch({
  swatch,
  isEditing,
  onOpenChange,
  onColorChange,
  onRemove,
  canRemove,
}: {
  swatch: PaletteSwatch;
  isEditing: boolean;
  onOpenChange: (open: boolean) => void;
  onColorChange: (color: string) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: swatch.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="group relative touch-none">
      <Popover open={isEditing} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            {...attributes}
            {...listeners}
            className={cn(
              "size-8 cursor-grab rounded-lg border-2 transition hover:scale-110 active:cursor-grabbing",
              isEditing
                ? "border-primary ring-2 ring-primary/20"
                : "border-border",
            )}
            style={{ backgroundColor: swatch.color }}
            title={swatch.color}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-auto border-0 bg-transparent p-0 shadow-none"
        >
          <ColorPickerPopover value={swatch.color} onChange={onColorChange} />
        </PopoverContent>
      </Popover>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          // Stop pointer events on the remove icon from initiating a drag
          // on the underlying swatch.
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute -right-1 -top-1 hidden size-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm group-hover:flex"
        >
          <X className="size-2.5" />
        </button>
      )}
    </div>
  );
}

function EditorPreferencesSection({
  orgId,
  profile,
  canEdit,
}: {
  orgId: Id<"organizations">;
  profile: BrandingProfilePreview | null | undefined;
  canEdit: boolean;
}) {
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.mutations.updateBrandingProfile,
    ),
  });

  /**
   * Palette swatches carry stable client-side `id`s so dnd-kit can track
   * them across reorders without relying on hex value (which can be
   * duplicated within a single palette). `id` is never persisted; only
   * the ordered `color` values are saved to `editorPresetColors`.
   */
  const buildSwatches = (colors: readonly string[]): PaletteSwatch[] =>
    colors.map((color) => ({ id: createSwatchId(), color }));

  const savedColors = profile?.editorPresetColors ?? [];
  const [localColors, setLocalColors] = useState<PaletteSwatch[]>(() =>
    buildSwatches(savedColors.length > 0 ? savedColors : DEFAULT_PRESET_COLORS),
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fontFamily, setFontFamily] = useState(profile?.editorFontFamily ?? "");
  const [lineHeight, setLineHeight] = useState(profile?.editorLineHeight ?? "");
  const [fontSize, setFontSize] = useState(profile?.editorFontSize ?? "");
  const [paragraphSpacingBefore, setParagraphSpacingBefore] = useState(
    profile?.editorParagraphSpacingBefore ?? "",
  );
  const [paragraphSpacingAfter, setParagraphSpacingAfter] = useState(
    profile?.editorParagraphSpacingAfter ?? "",
  );
  const [bodyTextColor, setBodyTextColor] = useState<string>(
    profile?.bodyTextColor ?? DEFAULT_BODY_TEXT_COLOR,
  );
  const [bodyColorPickerOpen, setBodyColorPickerOpen] = useState(false);

  useEffect(() => {
    const c = profile?.editorPresetColors ?? [];
    setLocalColors(buildSwatches(c.length > 0 ? c : DEFAULT_PRESET_COLORS));
    // We intentionally rebuild swatch ids whenever the persisted palette
    // changes; mid-drag updates are not possible because dnd-kit blocks
    // re-renders during an active drag.
  }, [profile?.editorPresetColors]);

  const sensors = useSensors(
    // Activation distance keeps single-clicks (open colour picker / remove)
    // from being mis-interpreted as drags.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  useEffect(() => {
    setFontFamily(profile?.editorFontFamily ?? "");
  }, [profile?.editorFontFamily]);

  useEffect(() => {
    setLineHeight(profile?.editorLineHeight ?? "");
  }, [profile?.editorLineHeight]);

  useEffect(() => {
    setFontSize(profile?.editorFontSize ?? "");
  }, [profile?.editorFontSize]);

  useEffect(() => {
    setParagraphSpacingBefore(profile?.editorParagraphSpacingBefore ?? "");
  }, [profile?.editorParagraphSpacingBefore]);

  useEffect(() => {
    setParagraphSpacingAfter(profile?.editorParagraphSpacingAfter ?? "");
  }, [profile?.editorParagraphSpacingAfter]);

  useEffect(() => {
    setBodyTextColor(profile?.bodyTextColor ?? DEFAULT_BODY_TEXT_COLOR);
  }, [profile?.bodyTextColor]);

  const handleSaveColors = async (swatches: PaletteSwatch[]) => {
    setLocalColors(swatches);
    try {
      await updateProfile({
        orgId,
        editorPresetColors: swatches.map((s) => s.color),
      });
      toast.success("Preset colours updated");
    } catch {
      toast.error("Failed to update preset colours.");
    }
  };

  const handleColorChange = (id: string, color: string) => {
    setLocalColors((prev) =>
      prev.map((s) => (s.id === id ? { ...s, color } : s)),
    );
  };

  /**
   * Commit the current local palette to the server and close the editor.
   * Called when the user clicks outside the popover so transient edits
   * (typing a hex value, dragging the picker) aren't persisted on every keystroke.
   */
  const handleColorCommit = () => {
    setEditingId(null);
    handleSaveColors(localColors);
  };

  const handleAddColor = () => {
    if (localColors.length >= 20) return;
    const newSwatch: PaletteSwatch = { id: createSwatchId(), color: "#000000" };
    setLocalColors([...localColors, newSwatch]);
    setEditingId(newSwatch.id);
  };

  const handleRemoveColor = (id: string) => {
    if (localColors.length <= 1) return;
    handleSaveColors(localColors.filter((s) => s.id !== id));
  };

  const handleResetColors = () => {
    handleSaveColors(buildSwatches(DEFAULT_PRESET_COLORS));
  };

  /**
   * Commit a reorder when the user drops a swatch on a new position.
   * `over.id === active.id` happens on a no-op drag (clicked but not moved
   * past activation distance, or dropped back in place) — bail in that case.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localColors.findIndex((s) => s.id === active.id);
    const newIndex = localColors.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    handleSaveColors(arrayMove(localColors, oldIndex, newIndex));
  };

  const handleFontChange = async (value: string) => {
    const resolved = value === "__default__" ? "" : value;
    setFontFamily(resolved);
    try {
      await updateProfile({
        orgId,
        editorFontFamily: resolved || undefined,
      });
      toast.success("Default font updated");
    } catch {
      toast.error("Failed to update default font.");
    }
  };

  const handleLineHeightChange = async (value: string) => {
    const resolved = value === "__default__" ? "" : value;
    setLineHeight(resolved);
    try {
      await updateProfile({
        orgId,
        editorLineHeight: resolved || undefined,
      });
      toast.success("Default line spacing updated");
    } catch {
      toast.error("Failed to update default line spacing.");
    }
  };

  const handleFontSizeChange = async (value: string) => {
    const resolved = value === "__default__" ? "" : value;
    setFontSize(resolved);
    try {
      await updateProfile({
        orgId,
        editorFontSize: resolved || undefined,
      });
      toast.success("Default font size updated");
    } catch {
      toast.error("Failed to update default font size.");
    }
  };

  const handleParagraphSpacingBeforeChange = async (value: string) => {
    const resolved = value === "__default__" ? "" : value;
    setParagraphSpacingBefore(resolved);
    try {
      await updateProfile({
        orgId,
        editorParagraphSpacingBefore: resolved || undefined,
      });
      toast.success("Default paragraph spacing updated");
    } catch {
      toast.error("Failed to update default paragraph spacing.");
    }
  };

  const handleParagraphSpacingAfterChange = async (value: string) => {
    const resolved = value === "__default__" ? "" : value;
    setParagraphSpacingAfter(resolved);
    try {
      await updateProfile({
        orgId,
        editorParagraphSpacingAfter: resolved || undefined,
      });
      toast.success("Default paragraph spacing updated");
    } catch {
      toast.error("Failed to update default paragraph spacing.");
    }
  };

  /**
   * Persist the chosen body text colour. Called when the colour-picker
   * popover closes so we don't write on every drag/keystroke.
   */
  const handleBodyTextColorCommit = async () => {
    setBodyColorPickerOpen(false);
    try {
      await updateProfile({
        orgId,
        bodyTextColor: bodyTextColor || DEFAULT_BODY_TEXT_COLOR,
      });
      toast.success("Body text colour updated");
    } catch {
      toast.error("Failed to update body text colour.");
    }
  };

  const handleBodyTextColorReset = async () => {
    setBodyTextColor(DEFAULT_BODY_TEXT_COLOR);
    try {
      await updateProfile({ orgId, bodyTextColor: DEFAULT_BODY_TEXT_COLOR });
      toast.success("Body text colour reset");
    } catch {
      toast.error("Failed to reset body text colour.");
    }
  };

  const isDefaultColors =
    localColors.length === DEFAULT_PRESET_COLORS.length &&
    localColors.every(
      (s, i) =>
        s.color.toLowerCase() === DEFAULT_PRESET_COLORS[i].toLowerCase(),
    );

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="px-6 py-3">
        <h2 className="text-base font-medium text-primary/70">
          Editor Preferences
        </h2>
        <p className="mt-0.5 text-xs text-primary/40">
          Customise the font and colour palette available when editing document
          templates. These defaults apply to all users in your organisation.
        </p>
      </div>

      {/* Default Font */}
      <div className="border-t border-border/50 px-6 py-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Type className="size-4 text-primary/40" />
              <span className="font-sans text-xs font-medium text-primary/60">
                Default Font
              </span>
            </div>
          </div>
          <Select
            value={fontFamily || "__default__"}
            onValueChange={handleFontChange}
            disabled={!canEdit}
          >
            <SelectTrigger className="h-8 w-[200px] shrink-0 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_FONT_OPTIONS.map((f) => (
                <SelectItem
                  key={f.label}
                  value={f.value || "__default__"}
                  className="text-xs"
                  style={f.value ? { fontFamily: f.value } : undefined}
                >
                  {f.value ? f.label : "Default (Arial)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {fontFamily && (
          <p
            className="mt-2 rounded-md bg-secondary/50 px-3 py-2 text-sm text-primary/60"
            style={{ fontFamily }}
          >
            The quick brown fox jumps over the lazy dog.
          </p>
        )}
      </div>

      {/* Default Line Spacing */}
      <div className="border-t border-border/50 px-6 py-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <WrapText className="size-4 text-primary/40" />
              <span className="font-sans text-xs font-medium text-primary/60">
                Default Line Spacing
              </span>
            </div>
            <p className="mt-1 text-[11px] text-primary/35">
              Applied to body text in the document editor and exported PDF/Word
              documents. Per-paragraph overrides from the editor still win.
            </p>
          </div>
          <Select
            value={lineHeight || "__default__"}
            onValueChange={handleLineHeightChange}
            disabled={!canEdit}
          >
            <SelectTrigger className="h-8 w-[200px] shrink-0 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EDITOR_LINE_HEIGHT_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.label}
                  value={opt.value || "__default__"}
                  className="text-xs"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Default Font Size */}
      <div className="border-t border-border/50 px-6 py-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <ALargeSmall className="size-4 text-primary/40" />
              <span className="font-sans text-xs font-medium text-primary/60">
                Default Font Size
              </span>
            </div>
            <p className="mt-1 text-[11px] text-primary/35">
              Applied to body text in the document editor and exported PDF/Word
              documents. Per-paragraph overrides from the editor still win.
            </p>
          </div>
          <Select
            value={fontSize || "__default__"}
            onValueChange={handleFontSizeChange}
            disabled={!canEdit}
          >
            <SelectTrigger className="h-8 w-[200px] shrink-0 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EDITOR_FONT_SIZE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.label}
                  value={opt.value || "__default__"}
                  className="text-xs"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Default Paragraph Spacing */}
      <div className="border-t border-border/50 px-6 py-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Rows3 className="size-4 text-primary/40" />
              <span className="font-sans text-xs font-medium text-primary/60">
                Default Paragraph Spacing
              </span>
            </div>
            <p className="mt-1 text-[11px] text-primary/35">
              Default space added above and below body paragraphs in the
              document editor and exported PDF/Word documents. Per-paragraph
              overrides from the editor still win.
            </p>
          </div>
          <div className="flex w-[200px] shrink-0 flex-col items-stretch gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-primary/40">
                Before
              </span>
              <Select
                value={paragraphSpacingBefore || "__default__"}
                onValueChange={handleParagraphSpacingBeforeChange}
                disabled={!canEdit}
              >
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDITOR_PARAGRAPH_SPACING_OPTIONS.map((opt) => (
                    <SelectItem
                      key={`before-${opt.label}`}
                      value={opt.value || "__default__"}
                      className="text-xs"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-primary/40">
                After
              </span>
              <Select
                value={paragraphSpacingAfter || "__default__"}
                onValueChange={handleParagraphSpacingAfterChange}
                disabled={!canEdit}
              >
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDITOR_PARAGRAPH_SPACING_OPTIONS.map((opt) => (
                    <SelectItem
                      key={`after-${opt.label}`}
                      value={opt.value || "__default__"}
                      className="text-xs"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Body Text Colour */}
      <div className="border-t border-border/50 px-6 py-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Palette className="size-4 text-primary/40" />
              <span className="font-sans text-xs font-medium text-primary/60">
                Body Text Colour
              </span>
            </div>
            <p className="mt-1 text-[11px] text-primary/35">
              Applied to body text in the editor and exported documents. Default
              is black; choose a softer grey if you prefer the historical look.
            </p>
          </div>
          <div className="flex w-[200px] shrink-0 flex-col items-end gap-2">
            {bodyTextColor.toLowerCase() !== DEFAULT_BODY_TEXT_COLOR &&
              canEdit && (
                <button
                  type="button"
                  onClick={handleBodyTextColorReset}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary/50 transition hover:bg-secondary"
                >
                  <RotateCcw className="size-3" />
                  Reset
                </button>
              )}
            {canEdit ? (
              <Popover
                open={bodyColorPickerOpen}
                onOpenChange={(open) => {
                  if (open) {
                    setBodyColorPickerOpen(true);
                  } else {
                    void handleBodyTextColorCommit();
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs transition hover:bg-secondary",
                      bodyColorPickerOpen
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-border",
                    )}
                  >
                    <span
                      className="size-4 rounded border border-border"
                      style={{ backgroundColor: bodyTextColor }}
                    />
                    <span className="font-mono text-[11px] text-primary/70">
                      {bodyTextColor.toUpperCase()}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  sideOffset={8}
                  className="w-auto border-0 bg-transparent p-0 shadow-none"
                >
                  <ColorPickerPopover
                    value={bodyTextColor}
                    onChange={setBodyTextColor}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1 text-xs">
                <span
                  className="size-4 rounded border border-border"
                  style={{ backgroundColor: bodyTextColor }}
                />
                <span className="font-mono text-[11px] text-primary/70">
                  {bodyTextColor.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preset Colors */}
      <div className="border-t border-border/50 px-6 py-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Palette className="size-4 text-primary/40" />
              <span className="font-sans text-xs font-medium text-primary/60">
                Colour Palette
              </span>
            </div>
            <p className="mt-1 text-[11px] text-primary/35">
              These colours appear as quick-pick swatches in the document
              editor.
              {canEdit
                ? " Click a swatch to change it, drag to reorder, or add/remove colours."
                : ""}
            </p>
          </div>
          {!isDefaultColors && canEdit && (
            <button
              type="button"
              onClick={handleResetColors}
              className="flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs text-primary/50 transition hover:bg-secondary"
            >
              <RotateCcw className="size-3" />
              Reset to defaults
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canEdit ? (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext
                items={localColors.map((s) => s.id)}
                strategy={horizontalListSortingStrategy}
              >
                {localColors.map((swatch) => (
                  <SortablePaletteSwatch
                    key={swatch.id}
                    swatch={swatch}
                    isEditing={editingId === swatch.id}
                    onOpenChange={(open) => {
                      if (open) {
                        setEditingId(swatch.id);
                      } else {
                        handleColorCommit();
                      }
                    }}
                    onColorChange={(next) => handleColorChange(swatch.id, next)}
                    onRemove={() => handleRemoveColor(swatch.id)}
                    canRemove={localColors.length > 1}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            localColors.map((swatch) => (
              <div
                key={swatch.id}
                className="size-8 rounded-lg border-2 border-border"
                style={{ backgroundColor: swatch.color }}
                title={swatch.color}
              />
            ))
          )}
          {localColors.length < 20 && canEdit && (
            <button
              type="button"
              onClick={handleAddColor}
              className="flex size-8 items-center justify-center rounded-lg border-2 border-dashed border-border text-primary/30 transition hover:border-primary/30 hover:text-primary/50"
              title="Add colour"
            >
              <Plus className="size-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
